import React, { useMemo, useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";

import { fetchAssignmentList } from "../assignmentSlice";
import { 
  retrieveCommentaryPDFAPI, 
  retrieveVocabularyRetryPDFAPI,
  retrieveVocabularyWordbookPDFAPI 
} from "../assignmentAPI";

import { formatDate, formatFullDateString, formatScore } from "../../../utils/textUtils";
import { refinePages } from "../../../utils/paginationUtils";

import { typeInfo } from "../../../constants/typeInfo";
import Pagination from "../../../components/Pagination";
import DescriptionIcon from "@mui/icons-material/DescriptionOutlined";
import RefreshIcon from "@mui/icons-material/Refresh";
import BookIcon from "@mui/icons-material/Book";
import PaperAssignmentModal from "./PaperAssignmentModal";

const FinishedAssignmentTable = () => {
  const dispatch = useDispatch();
  const [paperModalOpen, setPaperModalOpen] = useState(false);
  const [selectedPaperAssignment, setSelectedPaperAssignment] = useState(null);

  const { assignmentList, current_page, total_pages } = useSelector(
    (state) => state.assignment
  );
  const { student } = useSelector((state) => state.student);

  const visiblePages = useMemo(
    () => refinePages({ total_pages, current_page }),
    [current_page, total_pages]
  );

  const showCommentaryPDF = async (assignmentId) => {
    const pdfWindow = window.open();
    const response = await retrieveCommentaryPDFAPI(assignmentId, student.id);
    if (response.status !== 200) alert("해설지를 불러오는데 실패했습니다.");

    const file = new Blob([response.data], {
      type: "application/pdf",
    });
    const fileURL = URL.createObjectURL(file);
    pdfWindow.location.href = fileURL;
  };

  const showReviewPage = (assignmentId) => {
    const queryParams = new URLSearchParams(window.location.search);
    const session = queryParams.get("session");
    const reviewURL = `/review?id=${assignmentId}&session=${session}&type=student`;

    const reviewWindow = window.open();
    reviewWindow.location.href = reviewURL;
  };

  const showVocabularyRetryPDF = async (assignmentId, retryType) => {
    try {
      const pdfWindow = window.open();
      const response = await retrieveVocabularyRetryPDFAPI(assignmentId, student.id, retryType);
      
      if (response.status !== 200) {
        alert(`${retryType === 'wordbook' ? '단어장' : '재시험지'}를 불러오는데 실패했습니다.`);
        pdfWindow.close();
        return;
      }

      const file = new Blob([response.data], {
        type: "application/pdf",
      });
      const fileURL = URL.createObjectURL(file);
      pdfWindow.location.href = fileURL;
    } catch (error) {
      console.error("Error loading vocabulary retry PDF:", error);
      alert(`${retryType === 'wordbook' ? '단어장' : '재시험지'}를 불러오는데 실패했습니다.`);
    }
  };

  const showVocabularyWordbookPDF = async (assignmentId) => {
    try {
      const pdfWindow = window.open();
      const response = await retrieveVocabularyWordbookPDFAPI(assignmentId, student.id);
      
      if (response.status !== 200) {
        alert("단어장을 불러오는데 실패했습니다.");
        pdfWindow.close();
        return;
      }

      const file = new Blob([response.data], {
        type: "application/pdf",
      });
      const fileURL = URL.createObjectURL(file);
      pdfWindow.location.href = fileURL;
    } catch (error) {
      console.error("Error loading vocabulary wordbook PDF:", error);
      alert("단어장을 불러오는데 실패했습니다.");
    }
  };

  const handlePageClick = (page) => {
    dispatch(
      fetchAssignmentList({
        studentId: student.id,
        page,
        isFinished: true,
      })
    );
  };

  const handlePaperAssignmentClick = (assignment) => {
    setSelectedPaperAssignment(assignment);
    setPaperModalOpen(true);
  };

  const handlePaperModalClose = () => {
    setPaperModalOpen(false);
    setSelectedPaperAssignment(null);
  };

  useEffect(() => {
    if (student.id)
      dispatch(
        fetchAssignmentList({ studentId: student.id, isFinished: true })
      );
  }, [dispatch, student.id]);

  return (
    <div
      style={{
        maxWidth: "1400px",
        margin: "0 auto",
      }}
    >
      <h3
        style={{
          margin: "25px 0",
        }}
      >
        완료 과제 목록
      </h3>
      <div
        style={{
          backgroundColor: "#FFF",
          paddingBottom: "5px",
        }}
      >
        <table
          style={{
            width: "100%",
            borderCollapse: "collapse",
            backgroundColor: "#FFF",
          }}
        >
          <colgroup>
            <col style={{ width: "100px" }} />
            <col style={{ width: "100px" }} />
            <col style={{ width: "20px" }} />
            <col />
            <col style={{ width: "160px" }} />
            <col style={{ width: "60px" }} />
            <col style={{ width: "200px" }} />
          </colgroup>
          <tbody>
            <tr
              style={{
                borderBottom: "1px solid #EBEBEB",
                textAlign: "left",
                color: "#737373",
                fontSize: "16px",
              }}
            >
              <td style={{ textAlign: "center" }}>출제일</td>
              <td style={{ textAlign: "center" }}>마감일</td>
              <td></td>
              <td style={{ padding: "14px", paddingLeft: "10px" }}>학습지명</td>
              <td style={{ textAlign: "center" }}>학습내역 보기</td>
              <td style={{ textAlign: "center" }}>해설지/첨삭</td>
              <td style={{ textAlign: "center" }}>제출 시간</td>
            </tr>
            {assignmentList && assignmentList.length > 0 ? (
              assignmentList.map((assignment, index) => (
                <tr
                  key={index}
                  style={{
                    fontSize: "18px",
                  }}
                >
                  <td style={{ textAlign: "center" }}>
                    {formatDate(assignment.created_at)}
                  </td>
                  <td style={{ textAlign: "center" }}>
                    {assignment.closed_at ? formatDate(assignment.closed_at) : '-'}
                  </td>
                  <td></td>
                  <td style={{ padding: "10px" }}>
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "row",
                        gap: "10px",
                        alignItems: "center",
                      }}
                    >
                      {assignment.is_retry && (
                        <span style={{ 
                          backgroundColor: "#e8f5e8", 
                          color: "#2e7d32", 
                          padding: "4px 8px", 
                          borderRadius: "4px", 
                          fontSize: "12px",
                          marginLeft: "8px",
                          marginRight: "8px",
                          fontWeight: "bold"
                        }}>
                          복습 {assignment.retry_info?.retry_count || ''}회차
                        </span>
                      )}
                      {assignment.title}
                      <div
                        style={{
                          borderRadius: "5px",
                          padding: "2px",
                          width: "90px",
                          fontSize: "15px",
                          backgroundColor: typeInfo[assignment.type]?.badgeColor,
                          color: "#000",
                          display: "inline-block",
                          textAlign: "center",
                        }}
                      >
                        {assignment.type === 'class_content' 
                          ? assignment.class_content_data?.typeName 
                          : typeInfo[assignment.type]?.text}
                      </div>
                    </div>
                  </td>
                  <td style={{ padding: "10px", textAlign: "center" }}>
                    <button
                      style={{
                        backgroundColor: assignment.type === 'class_content' ? "#f5f5f5" : "#2D2D2D",
                        color: assignment.type === 'class_content' ? "#333" : "#FFFFFF",
                        border: "1px solid #EBEBEB",
                        borderRadius: "5px",
                        padding: "5px",
                        width: "120px",
                        cursor: assignment.type === 'class_content' ? "default" : "pointer",
                        fontSize: "16px",
                      }}
                      onClick={() => {
                        if (assignment.type === 'class_content') return;
                        if (assignment.type === 'paper_assignment') {
                          handlePaperAssignmentClick(assignment);
                        } else {
                          showReviewPage(assignment.id);
                        }
                      }}
                    >
                      {assignment.type === 'class_content' 
                        ? (assignment.score !== null ? `${formatScore(assignment.score)}/${formatScore(assignment.totalScore || 100)}` : '점수 없음')
                        : assignment.type === 'paper_assignment' 
                          ? (assignment.score !== null ? `${formatScore(assignment.score)}점` : '채점 중') 
                          : `${assignment.grade}점`}
                    </button>
                  </td>
                  <td style={{ textAlign: "center", padding: "10px" }}>
                    <div style={{ display: "flex", gap: "6px", justifyContent: "center", alignItems: "center" }}>
                      {assignment.type === 'class_content' ? (
                        // 수업결과의 경우 아무것도 표시하지 않음
                        <span style={{ color: "#999", fontSize: "14px" }}>-</span>
                      ) : assignment.type === 'paper_assignment' ? (
                        // 지필과제의 경우 첨삭 파일 표시
                        assignment.paper_assignment_data?.student_submission?.feedback_file_name ? (
                          <button
                            onClick={() => handlePaperAssignmentClick(assignment)}
                            style={{
                              backgroundColor: "#F6F8FA",
                              border: "1px solid #EBEBEB",
                              borderRadius: "5px",
                              padding: "5px",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              cursor: "pointer",
                            }}
                            title="첨삭 파일 보기"
                          >
                            <DescriptionIcon
                              style={{
                                fill: "#000000",
                                fontSize: "24px",
                                stroke: "#EBEBEB",
                              }}
                            />
                          </button>
                        ) : (
                          <span style={{ color: "#999", fontSize: "14px" }}>-</span>
                        )
                      ) : (
                        // 일반 과제의 경우 기존 로직
                        <>
                          {assignment.commentary_pdf_path !== "" && (
                            <button
                              onClick={() => showCommentaryPDF(assignment.id)}
                              style={{
                                backgroundColor: "#F6F8FA",
                                border: "1px solid #EBEBEB",
                                borderRadius: "5px",
                                padding: "5px",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                cursor: "pointer",
                              }}
                              title="해설지 보기"
                            >
                              <DescriptionIcon
                                style={{
                                  fill: "#000000",
                                  fontSize: "24px",
                                  stroke: "#EBEBEB",
                                }}
                              />
                            </button>
                          )}
                          {assignment.type === "vocabulary" && assignment.grade !== 100 && (
                            <>
                              <button
                                style={{
                                  backgroundColor: "#F6F8FA",
                                  border: "1px solid #EBEBEB",
                                  borderRadius: "5px",
                                  padding: "5px",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  cursor: "pointer",
                                }}
                                onClick={() => showVocabularyRetryPDF(assignment.id, "test")}
                                title="단어 재시험지 PDF 다운로드"
                              >
                                <RefreshIcon
                                  style={{
                                    fill: "#000000",
                                    fontSize: "24px",
                                    stroke: "#EBEBEB",
                                  }}
                                />
                              </button>
                              <button
                                style={{
                                  backgroundColor: "#F6F8FA",
                                  border: "1px solid #EBEBEB",
                                  borderRadius: "5px",
                                  padding: "5px",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  cursor: "pointer",
                                }}
                                onClick={() => showVocabularyWordbookPDF(assignment.id)}
                                title="틀린 단어 단어장 PDF 다운로드"
                              >
                                <BookIcon
                                  style={{
                                    fill: "#000000",
                                    fontSize: "24px",
                                    stroke: "#EBEBEB",
                                  }}
                                />
                              </button>
                            </>
                          )}
                        </>
                      )}
                    </div>
                  </td>
                  <td style={{ textAlign: "center" }}>
                    {assignment.type === 'class_content' 
                      ? formatFullDateString({
                          dateString: assignment.created_at,
                          defaultString: "",
                        })
                      : assignment.type === 'paper_assignment' 
                        ? (assignment.paper_assignment_data?.student_submission?.submitted_at 
                            ? formatFullDateString({
                                dateString: assignment.paper_assignment_data.student_submission.submitted_at,
                                defaultString: "",
                              })
                            : "")
                        : formatFullDateString({
                            dateString: assignment.finished_at,
                            defaultString: "",
                          })
                    }
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td
                  colSpan={7}
                  style={{ textAlign: "center", padding: "20px" }}
                >
                  과제 목록이 없습니다.
                </td>
              </tr>
            )}
          </tbody>
        </table>
        <Pagination
          visiblePages={visiblePages}
          current_page={current_page}
          handlePageClick={handlePageClick}
        />
      </div>

      <PaperAssignmentModal
        isOpen={paperModalOpen}
        onClose={handlePaperModalClose}
        assignment={selectedPaperAssignment}
      />
    </div>
  );
};

export default FinishedAssignmentTable;
